/**
 * Authentication Guard Component
 *
 * Provides route protection for authenticated users.
 * Redirects unauthenticated users to login page.
 */

import { LoadingSpinner } from '@/components/common/LoadingSpinner'
import { useTokenRefresh } from '@/hooks/use-token-refresh'
import { useAuthStore } from '@/store/auth'
import { useEffect, useState } from 'react'
import { Navigate, useLocation, useSearchParams } from 'react-router-dom'

interface AuthGuardProps {
  children: React.ReactNode
  /**
   * If true, allows access even without authentication (for dev mode testing)
   * Defaults to false in production
   */
  allowDevBypass?: boolean
}

/**
 * AuthGuard - Protects routes requiring authentication
 *
 * Features:
 * - Validates authentication state
 * - Redirects to login with return URL
 * - Shows loading state during auth check
 * - Optional dev mode bypass for testing
 */
export function AuthGuard({ children, allowDevBypass = false }: AuthGuardProps) {
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated)
  const isLoading = useAuthStore((s) => s.isLoading)
  const token = useAuthStore((s) => s.token)
  const location = useLocation()
  const [isInitialized, setIsInitialized] = useState(false)

  const isDev = import.meta.env.DEV

  // Proactive token refresh — keeps the session alive without 401 round-trips
  useTokenRefresh()

  // Wait for auth store to hydrate from localStorage
  useEffect(() => {
    // Give store time to rehydrate
    const timer = setTimeout(() => {
      setIsInitialized(true)
    }, 50)

    return () => clearTimeout(timer)
  }, [])

  // Show loading while checking auth state
  if (!isInitialized || isLoading) {
    return <LoadingSpinner className="min-h-screen" />
  }

  // Dev mode bypass - only in development and if explicitly allowed
  if (isDev && allowDevBypass && !isAuthenticated) {
    console.warn('[AuthGuard] Dev mode bypass active - authentication skipped')
    return <>{children}</>
  }

  // Not authenticated - redirect to login
  if (!isAuthenticated || !token) {
    // Store the intended destination to redirect back after login
    const returnUrl = location.pathname + location.search

    return (
      <Navigate
        to="/login"
        state={{ from: returnUrl }}
        replace
      />
    )
  }

  // Authenticated - render children
  return <>{children}</>
}

/**
 * GuestGuard - Protects routes that should only be accessible to guests
 *
 * Redirects authenticated users to dashboard.
 * Used for login, register, forgot-password pages.
 */
export function GuestGuard({ children }: { children: React.ReactNode }) {
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated)
  const isLoading = useAuthStore((s) => s.isLoading)
  const location = useLocation()
  const [searchParams] = useSearchParams()
  const [isInitialized, setIsInitialized] = useState(false)

  useEffect(() => {
    const timer = setTimeout(() => {
      setIsInitialized(true)
    }, 50)

    return () => clearTimeout(timer)
  }, [])

  if (!isInitialized || isLoading) {
    return <LoadingSpinner className="min-h-screen" />
  }

  // Already authenticated - redirect to intended destination.
  // Check returnUrl query param first (used by invitation accept flow),
  // then location state (used by AuthGuard redirect), then fallback to '/'.
  if (isAuthenticated) {
    const returnUrl = searchParams.get('returnUrl')
    const from = returnUrl
      || (location.state as { from?: string })?.from
      || '/'
    return <Navigate to={from} replace />
  }

  return <>{children}</>
}
